// Cache for encoded fonts
import {
	CURSIVE_FRAME_FALLBACK_FONT,
	CURSIVE_FRAME_FONT,
	DEFAULT_FRAME_FONT,
	TFrameFallbackFont,
	TFrameFont,
} from 'features/generator/qrFrames/qrFramesConstants'

// Cache for encoded fonts
const encodedFontCache: Record<
	TFrameFont | typeof CURSIVE_FRAME_FALLBACK_FONT,
	string | undefined
> = {
	[DEFAULT_FRAME_FONT]: undefined,
	[CURSIVE_FRAME_FONT]: undefined,
	[CURSIVE_FRAME_FALLBACK_FONT]: undefined,
}

const getEncodedFont = async (
	font: TFrameFont | typeof CURSIVE_FRAME_FALLBACK_FONT,
): Promise<string> => {
	if (encodedFontCache[font]) return encodedFontCache[font]!
	if (font === DEFAULT_FRAME_FONT) {
		const mod = await import('./encodedDefaultFont')
		encodedFontCache[font] = mod.ENCODED_DEFAULT_FONT
	} else if (font === CURSIVE_FRAME_FONT) {
		const mod = await import('./encodedCursiveFont')
		encodedFontCache[font] = mod.ENCODED_CURSIVE_FONT
	} else if (font === CURSIVE_FRAME_FALLBACK_FONT) {
		const mod = await import('./encodedCursiveFallbackFont')
		encodedFontCache[font] = mod.ENCODED_CURSIVE_FALLBACK_FONT
	}
	return encodedFontCache[font]!
}

const FONT_TO_NAME = {
	[DEFAULT_FRAME_FONT]: 'NotoSans',
	[CURSIVE_FRAME_FONT]: 'Allison',
	[CURSIVE_FRAME_FALLBACK_FONT]: 'Caveat',
} satisfies Record<TFrameFont, string>

// Map for optional fallback fonts
const FONT_FALLBACKS: Partial<Record<TFrameFont, TFrameFallbackFont>> = {
	[CURSIVE_FRAME_FONT]: CURSIVE_FRAME_FALLBACK_FONT,
}

const FONT_TO_WEIGHT = {
	[DEFAULT_FRAME_FONT]: 'bold',
	[CURSIVE_FRAME_FONT]: 'regular',
	[CURSIVE_FRAME_FALLBACK_FONT]: 'regular',
} satisfies Record<TFrameFont, string>

const FONT_TO_FORMAT = {
	[DEFAULT_FRAME_FONT]: 'woff',
	[CURSIVE_FRAME_FONT]: 'woff',
	[CURSIVE_FRAME_FALLBACK_FONT]: 'truetype',
} satisfies Record<TFrameFont, string>

const replaceFontVariableWithName = (svgText: string, font: TFrameFont) => {
	const fallbackFont = FONT_FALLBACKS[font]
	const fontNames = [FONT_TO_NAME[font]]
	if (fallbackFont) {
		fontNames.push(FONT_TO_NAME[fallbackFont])
	}
	const fontFamilyString = fontNames.join(', ')
	return svgText.replace(
		/font-family="[^"]*"/g,
		`font-family="${fontFamilyString}"`,
	)
}

const getEncodedFontStyleTag = async (font: TFrameFont) => {
	const encodedFont = await getEncodedFont(font)
	let styleTag = `
		<style>
			@font-face {
				font-family: ${FONT_TO_NAME[font]};
				src: url('${encodedFont}') format('${FONT_TO_FORMAT[font]}');
				font-weight: ${FONT_TO_WEIGHT[font]};
				font-style: normal;
				font-display: swap;
			}
	`
	const fallbackFont = FONT_FALLBACKS[font]
	if (fallbackFont) {
		const fallbackEncodedFont = await getEncodedFont(fallbackFont)
		styleTag += `
			@font-face {
				font-family: ${FONT_TO_NAME[fallbackFont]};
				src: url('${fallbackEncodedFont}') format('${FONT_TO_FORMAT[fallbackFont]}');
				font-weight: ${FONT_TO_WEIGHT[fallbackFont]};
				font-style: normal;
				font-display: swap;
			}
		`
	}
	styleTag += '\n\t\t</style>'
	return styleTag
}

const prependToSvg = (svgString: string, injectedContent: string) =>
	svgString.replace(
		/<svg([^>]*)>/i,
		(_, attrs) => `<svg${attrs}>${injectedContent}`,
	)

export const addEncodedFontToSvg = async (svgText: string, font: TFrameFont) =>
	prependToSvg(
		replaceFontVariableWithName(svgText, font),
		await getEncodedFontStyleTag(font),
	)
